import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { publish, PUB_SUB_EVENTS } from '@/scripts/core/global'
import {
	configPostRequest,
	showCartErrorNotification,
} from '@/scripts/core/cart-functions'

export class QuickOrderList extends UcoastEl {
	static htmlSelector = 'quick-order-list'
	productId: string
	sectionId: string
	submitButton: HTMLButtonElement
	messageEl: HTMLElement
	pendingUpdates: Map<string, number> = new Map()
	debounceTimer?: ReturnType<typeof setTimeout>

	constructor() {
		super()
		this.productId = q.ra(this, 'data-product-id')
		this.sectionId = q.ra(this, 'data-section-id')
		this.submitButton = q.rs('[data-quick-order-list-submit]', this)
		this.messageEl = q.rs('[data-quick-order-list-message]', this)

		this.setupEventListeners()
	}

	private setupEventListeners() {
		const inputs = q.ol<HTMLInputElement>('[data-quick-order-list-input]', this)
		if (!inputs) return

		inputs.forEach((input) => {
			input.addEventListener('change', this.onQuantityChange.bind(this))
		})
	}

	private onQuantityChange(event: Event) {
		const input = event.target as HTMLInputElement
		const variantId = input.dataset.variantId
		if (!variantId) return

		const newQty = parseInt(input.value) || 0
		const originalQty = parseInt(input.dataset.cartQuantity ?? '0')

		if (newQty !== originalQty) {
			this.pendingUpdates.set(variantId, newQty)
		} else {
			this.pendingUpdates.delete(variantId)
		}

		this.submitButton.disabled = this.pendingUpdates.size === 0
		this.updateTotalDisplay()

		// Debounce auto-submit
		if (this.debounceTimer) clearTimeout(this.debounceTimer)
		this.debounceTimer = setTimeout(() => {
			if (this.pendingUpdates.size > 0) {
				void this.submitUpdates()
			}
		}, 1000)
	}

	private updateTotalDisplay() {
		const totalItemsEl = q.os('[data-quick-order-list-total-items]', this)
		if (!totalItemsEl) return

		let totalItems = 0
		const inputs = q.ol<HTMLInputElement>('[data-quick-order-list-input]', this)
		inputs?.forEach((input) => {
			totalItems += parseInt(input.value) || 0
		})

		totalItemsEl.textContent = `${totalItems}`
	}

	private async submitUpdates() {
		if (this.pendingUpdates.size === 0) return

		this.submitButton.disabled = true
		this.submitButton.setAttribute('aria-busy', 'true')

		const updates: Record<string, number> = {}
		this.pendingUpdates.forEach((qty, variantId) => {
			updates[variantId] = qty
		})

		try {
			const response = await fetch(window.routes.cart_update_url, configPostRequest({
				updates,
			}))

			if (!response.ok) throw new Error(`${response.status}`)

			const cart = await response.json()

			// Update cart quantities in DOM
			const inputs = q.ol<HTMLInputElement>('[data-quick-order-list-input]', this)
			inputs?.forEach((input) => {
				const variantId = input.dataset.variantId
				if (!variantId) return
				const cartItem = cart.items?.find(
					(item: { variant_id: number }) => item.variant_id === parseInt(variantId)
				)
				const newCartQty = cartItem?.quantity ?? 0
				input.dataset.cartQuantity = `${newCartQty}`
				input.value = `${newCartQty}`
			})

			this.pendingUpdates.clear()
			this.showMessage(this.getSuccessMessage(updates))

			publish(PUB_SUB_EVENTS.cartUpdate, {
				source: 'cart-items',
			})
		} catch (error) {
			console.error('Quick order list update failed', error)
			showCartErrorNotification(window.cartStrings.error)
		} finally {
			this.submitButton.removeAttribute('aria-busy')
			this.submitButton.disabled = this.pendingUpdates.size === 0
		}
	}

	private getSuccessMessage(updates: Record<string, number>): string {
		let added = 0
		let removed = 0
		for (const [variantId, qty] of Object.entries(updates)) {
			const input = q.os<HTMLInputElement>(
				`[data-quick-order-list-input][data-variant-id="${variantId}"]`,
				this
			)
			const originalQty = parseInt(input?.dataset.cartQuantity ?? '0')
			if (qty > originalQty) added += qty - originalQty
			else if (qty < originalQty) removed += originalQty - qty
		}

		const str = window.quickOrderListStrings
		const messages: string[] = []
		if (added > 0) {
			messages.push(
				(added === 1 ? str.itemAdded : str.itemsAdded).replace('[quantity]', `${added}`)
			)
		}
		if (removed > 0) {
			messages.push(
				(removed === 1 ? str.itemRemoved : str.itemsRemoved).replace('[quantity]', `${removed}`)
			)
		}
		return messages.join('. ')
	}

	private showMessage(text: string) {
		this.messageEl.textContent = text
		this.messageEl.hidden = false
		setTimeout(() => {
			this.messageEl.hidden = true
		}, 5000)
	}
}
